import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FONTS = ["Montserrat", "Jost", "Marcellus", "Playfair Display", "Cormorant Garamond"];

const SECTIONS = [
  { id: "intro", label: "Accueil — Introduction" },
  { id: "highlights", label: "Accueil — Les 3 univers" },
  { id: "marquee", label: "Accueil — Bandeau défilant" },
  { id: "ambiance", label: "Accueil — L'ambiance (photos)" },
  { id: "avis", label: "Accueil — Avis Google" },
  { id: "horaires", label: "Accueil — Horaires & contact" },
  { id: "cta", label: "Accueil — Appel à la réservation" },
];

const DEFAULTS = {
  copper: "#C4704F",
  copperdark: "#A85B3D",
  gold: "#C6A87C",
  ink: "#050505",
  ivory: "#F5F5F0",
  radius: "0.5rem",
  font: "Montserrat",
  hero_scale: 1,
  btn_style: "filled",
  btn_border: "0",
  text_scale: 1,
  spacing_scale: 1,
  hidden_sections: [],
};

export default function DesignManager() {
  const [design, setDesign] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setDesign({ ...DEFAULTS, ...(r.data?.design || {}) }))
      .catch(() => setDesign({ ...DEFAULTS }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { design });
      toast.success("Design enregistré. Rechargez le site pour voir les changements.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (id, visible) =>
    setDesign((d) => ({
      ...d,
      hidden_sections: visible
        ? d.hidden_sections.filter((s) => s !== id)
        : [...d.hidden_sections, id],
    }));

  if (!design) return null;

  const colors = [
    { key: "copper", label: "Couleur principale (boutons, accents cuivrés)" },
    { key: "copperdark", label: "Couleur principale — survol des boutons" },
    { key: "gold", label: "Couleur dorée (titres, prix, surlignages)" },
    { key: "ink", label: "Couleur de fond du site" },
    { key: "ivory", label: "Couleur des textes" },
  ];

  return (
    <div className="max-w-2xl space-y-10" data-testid="design-manager">
      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Couleurs</h3>
        {colors.map((c) => (
          <div key={c.key} className="flex items-center gap-4">
            <input
              type="color"
              value={design[c.key]}
              onChange={(e) => setDesign((d) => ({ ...d, [c.key]: e.target.value }))}
              className="h-11 w-14 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1"
              data-testid={`design-color-${c.key}`}
            />
            <div className="flex-1">
              <p className="text-sm text-ivory/80">{c.label}</p>
              <p className="text-xs uppercase text-ivory/40">{design[c.key]}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Typographie</h3>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Police du site
          </label>
          <Select value={design.font} onValueChange={(v) => setDesign((d) => ({ ...d, font: v }))}>
            <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="design-font-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-coal text-ivory">
              {FONTS.map((f) => (
                <SelectItem key={f} value={f}>
                  {f}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Taille du titre d'accueil — {Math.round(design.hero_scale * 100)} %
          </label>
          <input
            type="range"
            min="0.7"
            max="1.3"
            step="0.05"
            value={design.hero_scale}
            onChange={(e) => setDesign((d) => ({ ...d, hero_scale: parseFloat(e.target.value) }))}
            className="w-full accent-[#C4704F]"
            data-testid="design-hero-scale"
          />
        </div>
      </div>

      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Arrondis & boutons
        </h3>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Arrondi des coins — {design.radius}
          </label>
          <input
            type="range"
            min="0"
            max="1.5"
            step="0.05"
            value={parseFloat(design.radius)}
            onChange={(e) => setDesign((d) => ({ ...d, radius: `${e.target.value}rem` }))}
            className="w-full accent-[#C4704F]"
            data-testid="design-radius"
          />
        </div>
      </div>

      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Boutons & textes
        </h3>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Style des boutons principaux
          </label>
          <Select value={design.btn_style} onValueChange={(v) => setDesign((d) => ({ ...d, btn_style: v }))}>
            <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="design-btn-style">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-coal text-ivory">
              <SelectItem value="filled">Rempli (fond cuivré)</SelectItem>
              <SelectItem value="outline">Contour (fond transparent)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Épaisseur des bordures de boutons — {design.btn_border} px
          </label>
          <input
            type="range"
            min="0"
            max="3"
            step="0.5"
            value={parseFloat(design.btn_border)}
            onChange={(e) => setDesign((d) => ({ ...d, btn_border: e.target.value }))}
            className="w-full accent-[#C4704F]"
            data-testid="design-btn-border"
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Taille des textes — {Math.round(design.text_scale * 100)} %
          </label>
          <input
            type="range"
            min="0.9"
            max="1.1"
            step="0.02"
            value={design.text_scale}
            onChange={(e) => setDesign((d) => ({ ...d, text_scale: parseFloat(e.target.value) }))}
            className="w-full accent-[#C4704F]"
            data-testid="design-text-scale"
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Espacement des sections (accueil) — {Math.round(design.spacing_scale * 100)} %
          </label>
          <input
            type="range"
            min="0.8"
            max="1.2"
            step="0.05"
            value={design.spacing_scale}
            onChange={(e) => setDesign((d) => ({ ...d, spacing_scale: parseFloat(e.target.value) }))}
            className="w-full accent-[#C4704F]"
            data-testid="design-spacing-scale"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Sections de la page d'accueil
        </h3>
        {SECTIONS.map((s) => {
          const visible = !design.hidden_sections.includes(s.id);
          return (
            <label key={s.id} className="flex cursor-pointer items-center gap-3 text-sm text-ivory/70" data-testid={`design-section-${s.id}`}>
              <input
                type="checkbox"
                checked={visible}
                onChange={(e) => toggleSection(s.id, e.target.checked)}
                className="h-4 w-4 accent-[#C4704F]"
              />
              {s.label}
              <span className={`text-xs ${visible ? "text-gold" : "text-ivory/30"}`}>
                {visible ? "visible" : "masquée"}
              </span>
            </label>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} className="btn-primary" data-testid="design-save-button">
        {saving ? "Enregistrement…" : "Enregistrer le design"}
      </button>
    </div>
  );
}
